import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Badge } from 'react-bootstrap';
import axios from 'axios';
import { PurchaseRequest } from '../types';
import { API_URL } from '../config';
import { useModal } from '../context/ModalContext';
import { getAuthToken } from '../utils/authToken';

interface DraftListModalProps {
  show: boolean;
  onHide: () => void;
  onSelectDraft: (draft: PurchaseRequest) => void;
  onDeleteDraft?: (draftId: number) => void;
}

const DraftListModal: React.FC<DraftListModalProps> = ({
  show,
  onHide,
  onSelectDraft,
  onDeleteDraft
}) => {
  const { showAlert, showConfirm } = useModal();
  const [drafts, setDrafts] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchDrafts();
    }
  }, [show]);

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/purchases/drafts`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      console.log('Rascunhos retornados pela API:', response.data);
      console.log('Quantidade de rascunhos:', response.data?.length || 0);
      setDrafts(response.data);
    } catch (error: any) {
      console.error('Erro ao buscar rascunhos:', error);
      if (error.response?.data) {
        console.error('Detalhes do erro (response.data):', error.response.data);
        console.error('Detalhes do erro (JSON):', JSON.stringify(error.response.data, null, 2));
        if (error.response.data.detail) {
          console.error('Mensagem de erro:', error.response.data.detail);
          if (Array.isArray(error.response.data.detail)) {
            error.response.data.detail.forEach((err: any, index: number) => {
              console.error(`Erro ${index + 1}:`, err);
            });
          }
        }
      }
      console.error('Status do erro:', error.response?.status);
      console.error('URL da requisição:', error.config?.url);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (draftId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await showConfirm({
      title: 'Excluir rascunho',
      message: 'Tem certeza que deseja excluir este rascunho?',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      // Deletar via endpoint de update mudando status para cancelado
      await axios.put(`${API_URL}/api/purchases/${draftId}`, {
        status: 'cancelado'
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setDrafts(drafts.filter(d => d.id !== draftId));
      if (onDeleteDraft) {
        onDeleteDraft(draftId);
      }
    } catch (error) {
      console.error('Erro ao excluir rascunho:', error);
      showAlert({
        title: 'Erro',
        message: 'Erro ao excluir rascunho. Tente novamente.',
        variant: 'danger',
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Meus Rascunhos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        ) : drafts.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">Você não tem rascunhos salvos.</p>
          </div>
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Título</th>
                <th>Criado em</th>
                <th>Atualizado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => (
                <tr
                  key={draft.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    onSelectDraft(draft);
                    onHide();
                  }}
                >
                  <td>
                    {draft.title || <span className="text-muted">Sem título</span>}
                  </td>
                  <td>{formatDate(draft.created_at)}</td>
                  <td>{formatDate(draft.updated_at)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectDraft(draft);
                        onHide();
                      }}
                      className="me-2"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => handleDelete(draft.id, e)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DraftListModal;
